import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare, Users, Filter, Search } from 'lucide-react';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import Pagination from '../../components/common/Pagination';
import { useFeedbackSummary, useFeedbackActivity } from '../../lib/api';
import { FeedbackItem } from '../../lib/types';
import { useAuth } from '../../contexts/AuthContext';

const FeedbackAnalytics: React.FC = () => {
    const { user } = useAuth();
    // Read-only insights accounts: the API drops the user ID, refuses filtering
    // by it, and masks emails/phone numbers/plates in comments.
    const isInsights = user?.role === 'insights';
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        feedbackType: '',
        userId: '',
        startDate: '',
        endDate: ''
    });

    const { summary, isLoading: summaryLoading, error: summaryError } = useFeedbackSummary();
    const { activity, isLoading: activityLoading, error: activityError } = useFeedbackActivity(page, 50, filters);

    const handleFilterChange = (key: string, value: string) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setPage(1);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const clearFilters = () => {
        setFilters({
            feedbackType: '',
            userId: '',
            startDate: '',
            endDate: ''
        });
        setPage(1);
    };

    if (summaryLoading || activityLoading) {
        return <LoadingState message="Loading feedback analytics..." />;
    }

    if (summaryError || activityError) {
        return <ErrorState error={(summaryError || activityError)?.message || 'An error occurred'} onRetry={() => window.location.reload()} />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Feedback Analytics</h1>
                    <p className="text-gray-600">User feedback analysis and insights</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MessageSquare className="h-4 w-4" />
                    <span>Total Feedback: {summary?.totalFeedback || 0}</span>
                </div>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="bg-white border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="h-5 w-5 text-blue-600" />
                            <span className="text-sm font-medium text-gray-600">Total Feedback</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{summary.totalFeedback}</div>
                        <div className="text-xs text-green-600 mt-1">+{summary.todayFeedback} today</div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <ThumbsUp className="h-5 w-5 text-green-600" />
                            <span className="text-sm font-medium text-gray-600">👍 Positive</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{summary.positiveFeedback}</div>
                        <div className="text-xs text-green-600 mt-1">+{summary.todayPositive} today</div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <ThumbsDown className="h-5 w-5 text-red-600" />
                            <span className="text-sm font-medium text-gray-600">👎 Negative</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{summary.negativeFeedback}</div>
                        <div className="text-xs text-red-600 mt-1">+{summary.todayNegative} today</div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="h-5 w-5 text-purple-600" />
                            <span className="text-sm font-medium text-gray-600">💬 Comments</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{summary.comments}</div>
                        <div className="text-xs text-green-600 mt-1">+{summary.todayComments} today</div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Users className="h-5 w-5 text-indigo-600" />
                            <span className="text-sm font-medium text-gray-600">Unique Users</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{summary.uniqueUsers}</div>
                        <div className="text-xs text-green-600 mt-1">+{summary.todayUniqueUsers} today</div>
                    </div>
                </div>
            )}

            {/* Filter Bar */}
            <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center gap-4 mb-4">
                    <Filter className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Feedback Type</label>
                        <select
                            value={filters.feedbackType}
                            onChange={(e) => handleFilterChange('feedbackType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Types</option>
                            <option value="up">👍 Positive</option>
                            <option value="down">👎 Negative</option>
                            <option value="comment">💬 Comments</option>
                        </select>
                    </div>

                    {!isInsights && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Filter by User ID"
                                    value={filters.userId}
                                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={clearFilters}
                            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Activity Feed */}
            {activity && (
                <div className="bg-white border rounded-lg">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900">Feedback Activity</h2>
                        <p className="text-sm text-gray-600">Recent feedback from users</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Timestamp
                                    </th>
                                    {!isInsights && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User ID
                                        </th>
                                    )}
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parking Lot
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Address
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Comment
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {activity.feedback.map((item: FeedbackItem, index: number) => (
                                    <tr key={item._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {new Date(item.timestamp).toLocaleString()}
                                        </td>
                                        {!isInsights && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.userId}
                                            </td>
                                        )}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {item.feedbackType === 'up' && '👍'}
                                            {item.feedbackType === 'down' && '👎'}
                                            {item.feedbackType === 'comment' && '💬'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {item.parkingLotName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {item.spotAddress}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                                            {item.comment || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 border-t">
                        <Pagination
                            pagination={activity.pagination}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeedbackAnalytics;
